import {
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  Hidden,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import styles from "./LearnAboutRecipe.module.css";
import ChessBroImage from "../../images/ChessBro.png";
import PauseBroImage from "../../images/PressPauseBro.png";
import BuildingBlock from "../../images/BuildingBlocks.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppDispatch } from "../../common/state/hooks";
import { useEffect } from "react";
import { update } from "../../common/state/Slices/HeaderMenuSlice";
import { Routes } from "../../routes/routes";
import {
  faEye,
  faGear,
  faUsers,
  faCubes,
  faFingerprint,
  faExchange,
  faRocket,
  faRobot,
  IconDefinition,
  faMicrochip,
  faImage,
  faShare,
  faPlay,
  faBarsProgress,
  faKitMedical
} from "@fortawesome/free-solid-svg-icons";
import { GIFrameModal } from "../../components/GIframeModal";

declare const genv: any;
const recipeInfoText = "Garuda Recipe is at the core of the Garuda2x ecosystem, transforming how users interact with diverse software tools. Recipes enable you to design, execute, and automate workflows by seamlessly connecting two or more Agents (formerly gadgets).";

const whatRecipeBuilderOffers: ListModel = {
  title: "Building Recipes: The Recipe Builder",
  subtitle:
    "The Recipe Builder is your gateway to creating workflows in the Garuda ecosystem. Its intuitive, drag-and-drop interface allows you to:",
  items: [
    {
      title: "Design & Customize",
      icon: faExchange,
      textContent:
        "Link two or more agents to create a workflow that meets your specific goals as well as define how these agents interact by specifying inputs, outputs, and dependencies.",
    },
    {
      title: "Visualize",
      icon: faImage,
      textContent:
        "See your workflow take shape in real time, ensuring clarity and accuracy.",
    },
    {
      title: "Save and Share",
      icon: faShare,
      textContent:
        "Save your Recipes for reuse or share them with collaborators to standardize processes.",
    },
  ],
};

const whatRecipePlayerOffers: ListModel = {
  title: "Running Recipes: The Recipe Runner",
  subtitle:
    "Once you’ve built your Recipe, the Recipe Runner takes over, providing a seamless platform to execute and monitor workflows:",
  items: [
    {
      title: "Execute End-to-End Workflows",
      icon: faPlay,
      textContent:
        "Play Recipes with a single click, navigating through interconnected Agents to gather insights and achieve outcomes.",
    },
    {
      title: "Monitor Progress",
      icon: faBarsProgress,
      textContent:
        "Track the execution of each step, ensuring everything runs smoothly.",
    },
    {
      title: "Iterate and Improve",
      icon: faCubes,
      textContent:
        "Adjust your Recipes based on results and feedback for continuous optimization.",
    },
  ],
};

interface WhyRecipesStandOutProps {
  title: string;
  subtitle: string;
  icon: IconDefinition;
}

const WhyRecipesStandOut = ({
  title,
  subtitle,
  icon,
}: WhyRecipesStandOutProps) => {
  return (
    <Grid item xs={12} md={6}>
      <Card>
        <CardHeader
          avatar={<FontAwesomeIcon icon={icon} size="2x" color="#44bcdb" />}
          title={
            <Typography variant="h5" sx={{ fontWeight: 500 }} gutterBottom>
              {title}
            </Typography>
          }
          subheader={
            <Typography variant="body2" sx={{ color: "#939191" }}>
              {subtitle}
            </Typography>
          }
        />
      </Card>
    </Grid>
  );
};

interface WhoBenefitsFromGarudaProps {
  title: string;
  subtitle: string;
  icon: IconDefinition;
}

const WhoBenefitsFromGaruda = ({
  title,
  subtitle,
  icon,
}: WhoBenefitsFromGarudaProps) => {
  return (
    <Grid item xs={12} md={4}>
      <Card>
        <CardHeader
          avatar={<FontAwesomeIcon icon={icon} size="2x" color="#44bcdb" />}
          title={
            <Typography variant="h5" sx={{ fontWeight: 500 }} gutterBottom>
              {title}
            </Typography>
          }
          subheader={
            <Typography variant="body2" sx={{ color: "#939191" }}>
              {subtitle}
            </Typography>
          }
        />
      </Card>
    </Grid>
  );
};

export interface GarudaListDisplayItemProps {
  icon: IconDefinition;
  title: string;
  textContent: string;
}

export const GarudaListDisplayItem = ({
  icon,
  title,
  textContent,
}: GarudaListDisplayItemProps) => {
  const theme = useTheme();

  return (
    <ListItem>
      <ListItemIcon>
        <FontAwesomeIcon
          icon={icon}
          size="2x"
          color={theme.palette.primary.main}
        />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {title}
          </Typography>
        }
        secondary={
          <Typography variant="body2" sx={{ color: "#939191" }}>
            {textContent}
          </Typography>
        }
      />
    </ListItem>
  );
};

interface ListModelItem {
  icon: IconDefinition;
  title: string;
  textContent: string;
}

interface ListModel {
  title: string;
  subtitle: string;
  items: ListModelItem[];
}

export interface GarudaListDisplayProps {
  model: ListModel;
  icon: any;
  actionButton: React.ReactNode
}

export const GarudaListDisplay = ({ model, icon, actionButton }: GarudaListDisplayProps) => {
  return (
    <Paper elevation={1} sx={{ margin: "0 0 2rem 0"}}>
      <Typography variant="h4" sx={{ mb: 1, ml: 3, pt: 5 }}>
        {model.title}
      </Typography>
      <Box display={"flex"} justifyContent={"center"}>
        <img src={icon} alt="recipe-builder" className={styles.icon}></img>
      </Box>
      <Typography variant="body1" style={{ padding: "1rem 1.5rem", margin: 0 }}>
        {model.subtitle}
      </Typography>
      <List>
        {model.items.map((item, index) => (
          <GarudaListDisplayItem
            key={index}
            icon={item.icon}
            title={item.title}
            textContent={item.textContent}
          />
        ))}
      </List>
      <Box display={"flex"} justifyContent={"center"} style={{ padding: "1rem" }}>
        {actionButton}
        </Box>
    </Paper>
  );
};

export const LearAboutRecipe = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  useEffect(() => {
    dispatch(
      update({
        selectedMenu: Routes.ABOUT_RECIPE,
      })
    );
  }, [dispatch]);

  return (
    <div className={styles.container}>
      <Box className={styles.topBannerArea}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={8} sm={8}>
            <Typography variant="h1" fontWeight={600} gutterBottom>
              Garuda Recipe:{" "}
              <span style={{ color: theme.palette.primary.main }}>
                The Power to Create, Automate, and Innovate
              </span>
            </Typography>
            <Typography variant="body1">{recipeInfoText}</Typography>
          </Grid>
            <Grid item md={4} lg={4} sm={4}>
              <Hidden smDown>
                <img
                  src={BuildingBlock}
                  alt="Building Block"
                  className={styles.buildingBlockImage}
                  data-testid="buidling-block-image"
                />
              </Hidden>
            </Grid>
        </Grid>
      </Box>
      <Typography variant="h2" sx={{ fontWeight: "500" }} gutterBottom>
        {"What is a Recipe ?"}
      </Typography>
      <Typography variant="body1">
        {
          "A Recipe in the Garuda ecosystem is more than just a workflow—it’s a dynamic, customizable sequence of steps that connects Agents to achieve specific outcomes. Whether you’re analyzing multi-omics data, managing clinical pipelines, or automating repetitive tasks, Recipes empower you to design efficient and reusable solutions tailored to your unique needs."
        }
      </Typography>
      <Typography
        variant="h2"
        sx={{ fontWeight: "500", margin: "3rem 0 2rem 0" }}
      >
        {"Why Garuda Recipes Stand Out"}
      </Typography>
      <Grid container spacing={2} sx={{ marginBottom: "2rem" }}>
        <WhyRecipesStandOut
          title="Interoperability Without Complexity"
          subtitle="Recipes enable seamless communication between Agents, regardless of the tools or systems they originate from. They eliminate the need for manual integration, allowing you to focus on outcomes rather than technical challenges."
          icon={faMicrochip}
        />
        <WhyRecipesStandOut
          title="Tailored for Automation"
          subtitle="By defining workflows in Recipes, you can automate repetitive tasks, reducing errors and saving time. Once built, a Recipe can be executed repeatedly with consistent results, making it ideal for large-scale operations."
          icon={faRobot}
        />
        <WhyRecipesStandOut
          title="Scalable and Flexible"
          subtitle="Recipes adapt to your needs, whether you’re working with a handful of tools or managing a complex network of Agents. They are designed to scale effortlessly as your projects grow."
          icon={faGear}
        />
        <WhyRecipesStandOut
          title="User-Friendly and Intuitive"
          subtitle="With the Recipe Builder and Recipe Runner, creating and executing Recipes is simple and straightforward, even for users without extensive technical expertise."
          icon={faUsers}
        />
      </Grid>
      <Divider sx={{ marginY: 2 }} />
      <Typography variant="h2" sx={{ fontWeight: "500", margin: "3rem 0 2rem 0" }} gutterBottom>
        {"Discover the Power of Recipes Today"}
      </Typography>
      <Grid container spacing={10}>
        <Grid item xs={12} md={6}>
          <GarudaListDisplay 
            model={whatRecipeBuilderOffers} 
            icon={ChessBroImage}
            actionButton={
              <GIFrameModal
                url={genv.ONLINE_RECIPE_BUILDER_VIDEO_GUIDE_URL}
                title="Show Demo"
                icon={faEye}
              />
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <GarudaListDisplay 
            model={whatRecipePlayerOffers} 
            icon={PauseBroImage}
            actionButton={
              <GIFrameModal
                url={genv.ONLINE_RECIPE_PLAYER_VIDEO_GUIDE_URL}
                title="Show Demo"
                icon={faEye}
              />
            }
          />
        </Grid>
      </Grid>
      <Divider sx={{ marginY: 2 }} />
      <Typography variant="h2" sx={{ fontWeight: "500", margin: "2rem 0" }}>
        {"Who Benefits from Garuda Recipes ?"}
      </Typography>
      <Grid container spacing={2} sx={{ marginBottom: "3rem" }}>
        <WhoBenefitsFromGaruda
          title="Researchers and Analysts"
          subtitle="Simplify complex data analysis by connecting tools and automating workflows, saving time and effort."
          icon={faRocket}
        />
        <WhoBenefitsFromGaruda
          title="Pharmaceutical and Biomedical Teams"
          subtitle="Manage large-scale pipelines for drug discovery, clinical trials, or data integration with efficiency and precision."
          icon={faKitMedical}
        />
        <WhoBenefitsFromGaruda
          title="IT and Data Science Teams"
          subtitle="Build and deploy production-ready workflows without the need for extensive coding, enabling faster delivery of results."
          icon={faFingerprint}
        />
      </Grid>
    </div>
  );
};
